import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Heading,
  Icon,
  IconButton,
  Input,
  Link,
  Spinner,
  Stack,
  Tag,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { API, graphqlOperation } from "aws-amplify";
import React, { useEffect, useMemo, useState } from "react";
import { BsBagCheckFill, BsInfoCircle } from "react-icons/bs";
import { FiExternalLink } from "react-icons/fi";
import { IoIosArrowBack } from "react-icons/io";
import { MdOutlineEmail } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";

import { apiAuth, apiGetAuth } from "../../Backend/api";
import { getGuest } from "../../graphql/queries";

const STATUS_LEGEND = [
  {
    label: "Authorized",
    colorScheme: "blue",
    description:
      "Your card has been authorized but hasn't been charged yet. The hold is released if the host doesn't confirm.",
  },
  {
    label: "Confirmed",
    colorScheme: "green",
    description:
      "The host confirmed your request and your card has been charged.",
  },
  {
    label: "Succeeded",
    colorScheme: "green",
    description: "The charge went through successfully and has been captured.",
  },
  {
    label: "Denied",
    colorScheme: "red",
    description:
      "The host declined the request. No charge was made to your card.",
  },
  {
    label: "Cancelled",
    colorScheme: "gray",
    description: "The request was cancelled. No charge was made to your card.",
  },
  {
    label: "Refunded",
    colorScheme: "purple",
    description: "The charge was refunded back to your card.",
  },
];
//lemaydenis86@gmail.com

const formatCents = (cents, currency) => {
  if (cents == null) return null;
  const amount = cents / 100;
  const code = (currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch (e) {
    return `$${amount.toFixed(2)}`;
  }
};

const statusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("pending") || s.includes("requested")) return "yellow";
  if (s.includes("author")) return "blue";
  if (s.includes("refund")) return "purple";
  if (s.includes("cancel")) return "gray";
  if (s.includes("den") || s.includes("declin") || s.includes("reject"))
    return "red";
  if (
    s.includes("confirm") ||
    s.includes("accept") ||
    s.includes("approved") ||
    s.includes("captur") ||
    s.includes("paid") ||
    s.includes("succe")
  )
    return "green";
  return "purple";
};

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const stripMarkdown = (s) => {
  if (!s) return s;
  return s
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/\n+/g, " ")
    .trim();
};

const kindLabel = (kind) => {
  if (kind === "charge") return "Deposit";
  if (kind === "upsell") return "Service";
  return null;
};

const getChargeId = (item) => {
  if (!item) return null;
  if (item.kind === "charge") return item.id;
  return item?.metadata?.chargeId || null;
};

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());

const PaymentCard = ({ item, onEmailReceipt }) => {
  const name = item?.name || "Payment";
  const description = stripMarkdown(item?.description);
  const priceText = formatCents(item?.amountCents, item?.currency);
  const capturedText =
    item?.capturedAmountCents != null && item.capturedAmountCents > 0
      ? formatCents(item.capturedAmountCents, item.currency)
      : null;
  const refundedText =
    item?.refundedAmountCents != null && item.refundedAmountCents > 0
      ? formatCents(item.refundedAmountCents, item.currency)
      : null;
  const status = item?.status || "authorized";
  const statusDisplay = item?.statusLabel || status;
  const createdAt = formatDate(item?.createdAt);
  const kind = kindLabel(item?.kind);

  // The hour tier the guest actually bought on an early check-in / late
  // check-out upsell. Only present when they picked one, which is what we
  // want — the sibling `metadata.offsetTime` falls back to the catalog's
  // default duration, so it would show a window on upsells that never
  // offered a choice.
  const durationHours = (() => {
    const parsed = Number(item?.metadata?.selectedHours);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  })();
  const durationText = durationHours
    ? `${durationHours} hour${durationHours === 1 ? "" : "s"}`
    : null;

  const brand = item?.metadata?.paymentMethodBrand;
  const last4 = item?.metadata?.paymentMethodLast4;
  const cardText =
    brand && last4
      ? `${brand.charAt(0).toUpperCase() + brand.slice(1)} ····${last4}`
      : null;

  const receiptUrl = item?.receiptUrl;
  const chargeId = getChargeId(item);

  return (
    <Box
      bg="whiteAlpha.100"
      border="1px solid"
      borderColor="whiteAlpha.200"
      borderRadius="16px"
      p={4}
    >
      <Flex justify="space-between" align="flex-start" gap={3} mb={2}>
        <Flex align="center" gap={2} flexWrap="wrap">
          {kind && (
            <Tag
              size="sm"
              variant="subtle"
              colorScheme={item?.kind === "charge" ? "orange" : "purple"}
              borderRadius="full"
              fontSize="10px"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing="0.06em"
            >
              {kind}
            </Tag>
          )}
          {durationText && (
            <Tag
              size="sm"
              variant="subtle"
              colorScheme="blue"
              borderRadius="full"
              fontSize="10px"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing="0.06em"
            >
              {durationText}
            </Tag>
          )}
        </Flex>
        <Badge
          colorScheme={statusColor(status)}
          variant="subtle"
          textTransform="capitalize"
          borderRadius="full"
          px={3}
          py={1}
          flexShrink={0}
        >
          {String(statusDisplay).toLowerCase()}
        </Badge>
      </Flex>

      <Text fontSize="15px" fontWeight={700}>
        {name}
      </Text>
      {description && (
        <Text fontSize="13px" color="whiteAlpha.700" mt={1} noOfLines={2}>
          {description}
        </Text>
      )}

      <Flex mt={3} gap={2} color="whiteAlpha.800" fontSize="13px" wrap="wrap">
        {priceText && <Text fontWeight={700}>{priceText}</Text>}
        {createdAt && <Text color="whiteAlpha.600">· {createdAt}</Text>}
      </Flex>

      {(capturedText || refundedText || cardText) && (
        <Stack spacing={1} mt={2} fontSize="12px" color="whiteAlpha.700">
          {capturedText && priceText && capturedText !== priceText && (
            <Text>Captured: {capturedText}</Text>
          )}
          {refundedText && <Text color="purple.200">Refunded: {refundedText}</Text>}
          {cardText && <Text>{cardText}</Text>}
        </Stack>
      )}

      {(receiptUrl || chargeId) && (
        <Flex mt={3} gap={4} wrap="wrap" align="center">
          {receiptUrl && (
            <Link
              href={receiptUrl}
              isExternal
              display="inline-flex"
              alignItems="center"
              gap={1}
              fontSize="13px"
              color="#817EFF"
              fontWeight={600}
              _hover={{ textDecoration: "underline" }}
            >
              View receipt <Icon as={FiExternalLink} boxSize={3} />
            </Link>
          )}
          {chargeId && onEmailReceipt && (
            <Button
              variant="link"
              size="sm"
              leftIcon={<MdOutlineEmail />}
              color="#817EFF"
              fontSize="13px"
              fontWeight={600}
              onClick={() => onEmailReceipt(chargeId)}
              _hover={{ textDecoration: "underline" }}
            >
              Email receipt
            </Button>
          )}
        </Flex>
      )}
    </Box>
  );
};

const TotalsSummary = ({ byCurrency }) => {
  const entries = Object.entries(byCurrency || {}).filter(
    ([, v]) =>
      (v?.authorizedCents || 0) > 0 ||
      (v?.capturedCents || 0) > 0 ||
      (v?.refundedCents || 0) > 0
  );
  if (entries.length === 0) return null;

  return (
    <Stack spacing={3} mb={5}>
      {entries.map(([currency, totals]) => {
        const hasRefunds = (totals?.refundedCents || 0) > 0;
        return (
          <Box
            key={currency}
            bg="whiteAlpha.50"
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius="16px"
            p={4}
          >
            <Flex justify="space-between" align="flex-start" gap={4}>
              <Box>
                <Text
                  fontSize="11px"
                  color="whiteAlpha.600"
                  letterSpacing="0.08em"
                  textTransform="uppercase"
                  fontWeight={700}
                >
                  Authorized · {currency.toUpperCase()}
                </Text>
                <Text fontSize="18px" fontWeight={800} color="#817EFF">
                  {formatCents(totals.authorizedCents, currency)}
                </Text>
              </Box>
              <Box textAlign="right">
                <Text
                  fontSize="11px"
                  color="whiteAlpha.600"
                  letterSpacing="0.08em"
                  textTransform="uppercase"
                  fontWeight={700}
                >
                  Charged
                </Text>
                <Text fontSize="18px" fontWeight={800}>
                  {formatCents(totals.capturedCents, currency)}
                </Text>
              </Box>
            </Flex>
            {hasRefunds && (
              <Flex justify="flex-end" mt={2}>
                <Text fontSize="12px" color="purple.200" fontWeight={600}>
                  Refunded: {formatCents(totals.refundedCents, currency)}
                </Text>
              </Flex>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

const StatusLegendDrawer = ({ isOpen, onClose }) => (
  <Drawer placement="bottom" onClose={onClose} isOpen={isOpen}>
    <DrawerOverlay />
    <DrawerContent
      maxH="75vh"
      borderTopRadius="2xl"
      maxW={{ base: "100%", md: "560px" }}
      mx="auto"
      mb={{ md: 8 }}
      borderRadius={{ md: "24px" }}
    >
      <Flex
        mx={3}
        mt={3}
        mb={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Text mr={4} fontSize="16px" fontWeight={800}>
          Status guide
        </Text>
        <DrawerCloseButton pos="relative" top={0} right={0} />
      </Flex>
      <DrawerBody px={3} pt={0} pb={4}>
        <Text fontSize="12px" color="whiteAlpha.700" mb={3}>
          Here's what each payment status means.
        </Text>
        <Stack spacing={2}>
          {STATUS_LEGEND.map((s) => (
            <Box
              key={s.label}
              bg="whiteAlpha.50"
              border="1px solid"
              borderColor="whiteAlpha.200"
              borderRadius="12px"
              px={3}
              py={2}
            >
              <Flex align="center" mb={1}>
                <Badge
                  colorScheme={s.colorScheme}
                  variant="subtle"
                  borderRadius="full"
                  px={2}
                  py={0.5}
                  fontSize="10px"
                  textTransform="capitalize"
                >
                  {s.label.toLowerCase()}
                </Badge>
              </Flex>
              <Text fontSize="12px" color="whiteAlpha.800" lineHeight="1.4">
                {s.description}
              </Text>
            </Box>
          ))}
        </Stack>
      </DrawerBody>
    </DrawerContent>
  </Drawer>
);

const ReceiptEmailDrawer = ({
  isOpen,
  onClose,
  chargeId,
  defaultEmail,
  hasDefaultEmail,
}) => {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setEmail(defaultEmail || "");
      setError("");
      setLoading(false);
    }
  }, [isOpen, defaultEmail]);

  const handleSend = async () => {
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!chargeId) {
      setError("Missing charge reference. Please refresh and try again.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await apiAuth(`/transactions/${chargeId}/send-receipt`, {
        email: trimmed,
      });
      toast({
        title: "Receipt sent",
        description: `We've emailed the receipt to ${trimmed}.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onClose();
    } catch (err) {
      console.error("Failed to send receipt:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't send the receipt. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const title = hasDefaultEmail ? "Email your receipt" : "Add your email";
  const description = hasDefaultEmail
    ? "We'll send the Stripe receipt to the email below. Change it if you want it sent somewhere else."
    : "We don't have an email on file yet. Enter the address you'd like the receipt sent to.";

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !loading) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Drawer placement="bottom" onClose={onClose} isOpen={isOpen}>
      <DrawerOverlay />
      <DrawerContent
        maxH="90vh"
        borderTopRadius="2xl"
        bg="gray.900"
        color="white"
        maxW={{ base: "100%", md: "560px" }}
        mx="auto"
        mb={{ md: 8 }}
        borderRadius={{ md: "24px" }}
      >
        <Flex
          mx={3}
          my={4}
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex align="center" gap={3}>
            <Flex
              align="center"
              justify="center"
              boxSize="36px"
              borderRadius="full"
              bg="whiteAlpha.100"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Icon as={MdOutlineEmail} boxSize={5} color="#817EFF" />
            </Flex>
            <Text fontSize="18px" fontWeight={800}>
              {title}
            </Text>
          </Flex>
          <DrawerCloseButton pos="relative" top={0} right={0} />
        </Flex>

        <DrawerBody px={4} pt={0} pb={6}>
          <Text fontSize="13px" color="whiteAlpha.700" mb={4}>
            {description}
          </Text>

          <Box
            bg="whiteAlpha.50"
            border="1px solid"
            borderColor={error ? "red.400" : "whiteAlpha.200"}
            borderRadius="14px"
            p={3}
            transition="border-color 0.15s ease"
          >
            <Text
              fontSize="11px"
              color="whiteAlpha.600"
              letterSpacing="0.08em"
              textTransform="uppercase"
              fontWeight={700}
              mb={1}
            >
              Email address
            </Text>
            <Input
              variant="unstyled"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
              }}
              onKeyDown={handleKeyDown}
              placeholder="you@example.com"
              color="white"
              fontSize="15px"
              fontWeight={600}
              type="email"
              autoFocus={!hasDefaultEmail}
              _placeholder={{ color: "whiteAlpha.500" }}
            />
          </Box>

          {error && (
            <Alert
              status="error"
              borderRadius="12px"
              mt={3}
              bg="red.900"
              color="red.100"
            >
              <AlertIcon color="red.300" />
              <Text fontSize="13px">{error}</Text>
            </Alert>
          )}

          <Stack spacing={2} mt={5}>
            <Button
              w="full"
              bg="#817EFF"
              color="white"
              h="48px"
              borderRadius="14px"
              fontSize="14px"
              fontWeight={700}
              leftIcon={<MdOutlineEmail />}
              _hover={{ bg: "#615edc" }}
              _active={{ bg: "#4b49c4" }}
              onClick={handleSend}
              isLoading={loading}
              loadingText="Sending..."
            >
              Send receipt
            </Button>
            <Button
              w="full"
              variant="ghost"
              color="whiteAlpha.800"
              h="44px"
              borderRadius="14px"
              fontSize="13px"
              fontWeight={600}
              _hover={{ bg: "whiteAlpha.100" }}
              _active={{ bg: "whiteAlpha.200" }}
              onClick={onClose}
              isDisabled={loading}
            >
              Cancel
            </Button>
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

const EmptyState = () => (
  <Flex flex={1} align="center" justify="center" px={6} py={12}>
    <VStack spacing={4} textAlign="center">
      <Flex
        align="center"
        justify="center"
        boxSize="80px"
        borderRadius="full"
        bg="whiteAlpha.100"
      >
        <Icon as={BsBagCheckFill} boxSize={10} color="#817EFF" />
      </Flex>
      <Text fontSize="20px" fontWeight={700}>
        No payments yet
      </Text>
      <Text fontSize="14px" color="whiteAlpha.700" maxW="280px">
        When you pay for a service during your stay, it will show up here.
      </Text>
    </VStack>
  </Flex>
);

export const Payments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const booking = location.state?.booking;
  const bookingId = booking?.id;
  const guestId = booking?.guestID;

  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [totals, setTotals] = useState(null);
  const [guestEmail, setGuestEmail] = useState(null);
  const [receiptChargeId, setReceiptChargeId] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isReceiptOpen,
    onOpen: onReceiptOpen,
    onClose: onReceiptClose,
  } = useDisclosure();

  const goBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!bookingId) {
        setPayments([]);
        setTotals(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await apiGetAuth(`/v2/upsells/booking/${bookingId}/all`);
        if (cancelled) return;
        const list = res?.data?.purchases || [];
        setPayments(Array.isArray(list) ? list : []);
        setTotals(res?.data?.totals || null);
      } catch (err) {
        console.error("Failed to fetch booking payments:", err);
        if (!cancelled) {
          setPayments([]);
          setTotals(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  useEffect(() => {
    let cancelled = false;

    const loadGuest = async () => {
      if (!guestId) return;
      try {
        const res = await API.graphql(graphqlOperation(getGuest, { id: guestId }));
        if (cancelled) return;
        const email = res?.data?.getGuest?.email;
        if (email && !email.toLowerCase().includes("@noemail.com")) {
          setGuestEmail(email);
        }
      } catch (err) {
        console.error("Failed to fetch guest email:", err);
      }
    };

    loadGuest();

    return () => {
      cancelled = true;
    };
  }, [guestId]);

  const handleEmailReceipt = (chargeId) => {
    setReceiptChargeId(chargeId);
    onReceiptOpen();
  };

  const handleReceiptClose = () => {
    onReceiptClose();
    setReceiptChargeId(null);
  };

  const hasPayments = payments.length > 0;
  const hasDefaultEmail = useMemo(() => !!guestEmail, [guestEmail]);

  return (
    <Flex
      direction="column"
      minH="100vh"
      w="100%"
      bg="gray.900"
      color="white"
      position="relative"
    >
      <Flex
        w="100%"
        top={5}
        position="absolute"
        px={2}
        zIndex={1}
        justify="space-between"
        align="center"
      >
        <Button
          leftIcon={<IoIosArrowBack />}
          bg={"#817EFF"}
          h={"45px"}
          variant="solid"
          borderRadius={"20px"}
          fontSize={"12px"}
          _active={{ bg: "#615edc" }}
          _hover={{ bg: "#615edc" }}
          onClick={goBack}
        >
          Back
        </Button>

        <IconButton
          aria-label="Status guide"
          icon={<BsInfoCircle />}
          onClick={onOpen}
          bg="whiteAlpha.100"
          color="white"
          border="1px solid"
          borderColor="whiteAlpha.300"
          borderRadius="full"
          h="45px"
          w="45px"
          fontSize="20px"
          _hover={{ bg: "whiteAlpha.200" }}
          _active={{ bg: "whiteAlpha.300" }}
        />
      </Flex>

      <Box pt="80px" pb={8} px={4} flex={1} display="flex" flexDirection="column">
        <Heading fontSize="24px" fontWeight={800} mb={6}>
          Payments
        </Heading>

        {loading && (
          <Flex justify="center" py={10}>
            <Spinner color="#817EFF" />
          </Flex>
        )}

        {!loading && hasPayments && (
          <TotalsSummary byCurrency={totals?.byCurrency} />
        )}

        {!loading && !hasPayments && <EmptyState />}

        {!loading && hasPayments && (
          <Stack spacing={3}>
            {payments.map((item, idx) => (
              <PaymentCard
                key={item?.id || idx}
                item={item}
                onEmailReceipt={handleEmailReceipt}
              />
            ))}
          </Stack>
        )}
      </Box>

      <StatusLegendDrawer isOpen={isOpen} onClose={onClose} />

      <ReceiptEmailDrawer
        isOpen={isReceiptOpen}
        onClose={handleReceiptClose}
        chargeId={receiptChargeId}
        defaultEmail={guestEmail}
        hasDefaultEmail={hasDefaultEmail}
      />
    </Flex>
  );
};
